import React, {
  useCallback,
  useMemo,
  useState,
} from 'react';
import {
  Identifier,
  InputHelperText,
  InputProps,
  RecordContextProvider,
  useInput,
  useRecordContext,
} from 'react-admin';
import {
  FormControl,
  IconButton,
  FormHelperText,
  InputAdornment,
  FilledInput,
  InputLabel,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

import ReferenceEntityField, { EntityContext } from './ReferenceEntityField';
import { EntityTypes } from '../constants/entityTypes';
import EntitySelectionDialog from '../components/entities/EntitySelectionDialog';
import { getEntityDisplayName } from './EntityDisplayNameField';
import { IIndividual } from '../utilities/schemas/individual';

const EntityInputContent = ({
  id,
  endAdornment,
}: {
  id: string;
  label?: React.ReactNode;
  endAdornment?: React.ReactNode;
}) => {
  const record = useRecordContext<IIndividual>();

  const entityContextValue = React.useContext(EntityContext);

  const { type } = entityContextValue ?? {};
  const displayName = (record && type) ? getEntityDisplayName({ record, type }) : '';

  return (
    <FilledInput
      id={id}
      value={displayName}
      size="small"
      readOnly
      endAdornment={(
        <InputAdornment position="end">
          {endAdornment}
        </InputAdornment>
      )}
    />
  );
};

const EntityInput = ({
  id,
  label,
  endAdornment,
}: {
  id: string;
  label?: React.ReactNode;
  endAdornment?: React.ReactNode;
}) => {
  const record = useRecordContext<{ id: string; type: EntityTypes }>();

  if (!record || !record.id || !record.type) {
    return (
      <FilledInput
        id={id}
        value=""
        readOnly
        size="small"
        endAdornment={(
          <InputAdornment position="end">
            {endAdornment}
          </InputAdornment>
        )}
      />
    );
  }

  return (
    <ReferenceEntityField source="" linkType={false} emptyText="" useBaseVersion>
      <EntityInputContent id={id} label={label} endAdornment={endAdornment} />
    </ReferenceEntityField>
  );
};

const ReferenceEntityInputV2 = (props: InputProps & {
  allowedTypes?: EntityTypes[];
  fullWidth?: boolean;
}) => {
  const {
    label,
    helperText,
    source,
    allowedTypes,
    fullWidth,
  } = props;
  const idSource = source ? `${source}.id` : 'id';
  const typeSource = source ? `${source}.type` : 'type';
  const {
    id: fieldId,
    field: {
      onChange: onChangeId,
      value: valueId,
    },
    fieldState: { error, invalid, isTouched },
    formState: { isSubmitted },
    isRequired,
  } = useInput({
    ...props,
    source: idSource,
  });

  const {
    field: {
      onChange: onChangeType,
      value: valueType,
    },
  } = useInput({
    ...props,
    source: typeSource,
  });

  const renderHelperText = (helperText !== false && helperText !== undefined)
    || ((isTouched || isSubmitted) && invalid);

  const [open, setOpen] = useState(false);
  const handleStartEdit = useCallback(() => {
    setOpen(true);
  }, []);
  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);
  const handleSelect = useCallback((data: { id: Identifier; type: EntityTypes }) => {
    onChangeId(data.id);
    onChangeType(data.type);
    setOpen(false);
  }, [onChangeId, onChangeType]);
  const handleClear = useCallback(() => {
    onChangeId(null);
    onChangeType(null);
  }, [onChangeId, onChangeType]);

  const currentValue = useMemo(() => ({
    id: valueId,
    type: valueType,
  }), [valueId, valueType]);

  return (
    <>
      <FormControl
        fullWidth={fullWidth}
        required={isRequired}
        error={(isTouched || isSubmitted) && invalid}
        variant="filled"
      >
        <InputLabel htmlFor={fieldId}>
          {label}
        </InputLabel>
        <RecordContextProvider value={currentValue}>
          <EntityInput
            id={fieldId}
            label={label}
            endAdornment={(
              <>
                <IconButton size="small" onClick={handleStartEdit} color="primary">
                  {valueId ? <EditIcon /> : <AddCircleOutlineIcon />}
                </IconButton>
                {!!valueId && (
                  <IconButton size="small" onClick={handleClear} color="default">
                    <HighlightOffIcon />
                  </IconButton>
                )}
              </>
            )}
          />
        </RecordContextProvider>
        <FormHelperText>
          {renderHelperText && (
            <InputHelperText
              error={error?.message}
              helperText={helperText}
            />
          )}
        </FormHelperText>
      </FormControl>
      <EntitySelectionDialog
        currentValue={valueId}
        open={open}
        handleClose={handleClose}
        handleSelect={handleSelect}
        label={label}
        allowedTypes={allowedTypes}
      />
    </>
  );
};

export default ReferenceEntityInputV2;
